<template>
  <TourFilterPopupWrapper
    is-fullscreen
    show-close-button
    :title="t('title.header')"
    filter-key="modal"
    :is-disabled-reset-button="isDisabledResetButton"
    :modal-size="modalSize"
    modal-class="modal-filters"
    is-modal-only
    @open="openModalContent"
  >
    <template #trigger="{ open }">
      <div class="filters-open-btn">
        <SkeletonBlock
          :is-active="isSkeletonActive"
          :width="filterButtonSkeletonWidth"
          :height="48"
        >
          <UiButton
            v-if="!isSkeletonActive"
            :text="t('title.header')"
            :variant="UiButtonVariant.OUTLINE"
            :color-mode="UiButtonColorMode.NEUTRAL"
            :size="currentSize"
            @clicked="open"
          >
            <template #icon><ModalFilterBtnIcon /></template>
          </UiButton>
          <div
            v-if="!isSkeletonActive && filtersCount"
            class="filters-count"
          >
            {{ filtersCount }}
          </div>
        </SkeletonBlock>
      </div>
    </template>

    <TourModalFilterContent
      v-if="isFilterContentLoaded"
      :display-currency="props.displayCurrency"
      :is-price-loading="props.isPriceLoading"
      :duration-params="props.durationParams"
      :price="props.price"
      @display-currency-change="emit('display-currency-change', $event)"
    />
  </TourFilterPopupWrapper>
</template>

<script setup lang="ts">
  import { defineAsyncComponent } from 'vue';
  import { TourFilterPopupWrapper } from '@/entities/filter/popup-wrapper';
  import { UiButton, UiButtonColorMode, UiButtonSize, UiButtonVariant } from '@experience/core-button-ui';
  import { ModalFilterBtnIcon } from '@/shared/ui/icons';
  import { useI18n } from '@experience/i18n';
  import ru from '../../i18n/ru.json';
  import { type TTourFiltersState, useTourFiltersStore } from '@/entities/filter/store/tour';
  import { Sort, type TDisplayCurrencyData } from '@/widgets/filtersBlock/types';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { UiModalSize } from '@experience/core-modal-ui';
  import { useScreenSize } from '@experience/shared';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { useDisplayCurrencyFilterSogu } from '@/widgets/filtersBlock/composables/useDisplayCurrencyFilterSogu.ts';

  /**
   * Лениво загружает содержимое модалки со всеми туровыми фильтрами после первого открытия.
   */
  const TourModalFilterContent = defineAsyncComponent(() => import('./TourModalFilterContent.vue'));

  interface Props {
    isScroll: boolean;
    price: {
      histogram: Record<string, number>;
      minPrice: number;
      maxPrice: number;
    };
    durationParams: Record<string, number>;
    displayCurrency: TDisplayCurrencyData;
    isPriceLoading: boolean;
    onOpen?: (isForced: boolean) => void;
  }

  interface Emits {
    (e: 'display-currency-change', isoCode: string): void;
  }

  const { isSkeletonActive } = useListingSkeleton();

  const props = defineProps<Props>();
  const emit = defineEmits<Emits>();

  const { trackDisplayCurrencyFilterView } = useDisplayCurrencyFilterSogu();

  const filtersStore = useTourFiltersStore();
  const isFilterContentLoaded = ref(false);

  const { isMobile } = useScreenSize();

  const modalSize = computed(() => (isMobile.value ? UiModalSize.FULLSCREEN : UiModalSize.S));
  const filterButtonSkeletonWidth = computed(() => (isMobile.value ? '100%' : 135));

  const countCortegeFilter = (value: Array<string | number | null>) => {
    return value?.some((item) => item !== null) ? 1 : 0;
  };

  const countBooleanFilter = (value: boolean | null) => {
    return value ? 1 : 0;
  };

  const countArrayFilter = (value: Array<string>) => {
    return value?.length ? 1 : 0;
  };

  function countAppliedFilters(state: TTourFiltersState) {
    let count = 0;

    Object.entries(state).forEach(([key, value]) => {
      switch (key) {
        case 'price':
        case 'duration':
        case 'comfort_type':
        case 'activity_level_category':
        case 'accommodation_type':
          count += countCortegeFilter(value as Array<string | null | number>);
          break;
        case 'date':
          break;
        case 'persons':
          count += (value as number) > 1 ? 1 : 0;
          break;
        case 'tourFormat':
          count += countArrayFilter(value as Array<string>);
          break;
        case 'childrenAllowed':
          count += countBooleanFilter(value as boolean);
          break;
        default:
          count += 0;
      }
    });

    return count;
  }

  const filtersCount = computed(() => {
    return countAppliedFilters(filtersStore.searchState);
  });

  const filtersSelectedCount = computed(() => {
    return countAppliedFilters(filtersStore.state);
  });

  const isDisabledResetButton = computed(() => {
    return (
      filtersSelectedCount.value < 1 && (!filtersStore.state.sorting || filtersStore.state.sorting === Sort.popularity)
    );
  });

  const { t } = useI18n({
    inheritLocale: true,
    messages: {
      ru,
    },
  });

  const currentSize = computed(() => {
    if (isMobile.value) {
      return UiButtonSize.M;
    }

    return props.isScroll ? UiButtonSize.S : UiButtonSize.M;
  });

  /**
   * Разрешает загрузку тяжелого содержимого модалки только после открытия
   * и трекает просмотр фильтра валюты.
   */
  const openModalContent = (isForced: boolean) => {
    isFilterContentLoaded.value = true;
    props.onOpen?.(isForced);
    trackDisplayCurrencyFilterView(props.displayCurrency.iso ?? '');
  };
</script>

<style scoped lang="scss">
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;
  @use '@experience/design-tokens/typography' as typography;

  .filters-open-btn {
    position: relative;
    display: flex;
    align-items: center;
  }

  .filters-count {
    position: absolute;
    top: -6px;
    right: -6px;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 20px;
    height: 20px;
    border: 2px solid var(--comp-badge-color-border-default);
    border-radius: var(--alias-radius-rounded);
    background: var(--comp-badge-color-background-neutral);
    color: var(--comp-badge-color-text);
    font-weight: var(--global-typography-weight-regular);
    font-style: normal;
    font-size: 12px;
    line-height: 16px;
    transition: 0.3s all cubic-bezier(0.77, 0, 0.18, 1);
  }

  :deep(.footer-persons) {
    margin-bottom: 0;
  }
</style>
